import { Box } from "@mui/material";
import Link from "next/link";
import { useId, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_MEDIA,
  SITE_TYPE,
  SITE_WRAP,
} from "src/domains/shared/constants/styles/site";
import {
  BRIDGE_DURATIONS,
  BridgeSceneContent,
} from "../scenes/BridgeSceneContent";
import { SceneSection, type SceneStepText } from "../scenes/SceneSection";
import {
  TERRAIN_DURATIONS,
  TerrainSceneContent,
} from "../scenes/TerrainSceneContent";
import { ClosingContact } from "../siteKit/ClosingContact";
import { FeatureRow, FeatureRows } from "../siteKit/FeatureRows";
import { LinkCard } from "../siteKit/LinkCard";
import { SiteFileImage } from "../siteKit/media/SiteFileImage";
import { SiteImage } from "../siteKit/media/SiteImage";
import { SiteVideo } from "../siteKit/media/SiteVideo";
import { revealRef } from "../siteKit/motion/reveal";
import { PageHero } from "../siteKit/PageHero";
import { SectionHead } from "../siteKit/SectionHead";
import { SiteSection } from "../siteKit/SiteSection";
import { SITE_BUTTON_STYLES } from "../siteKit/siteButtonStyles";
import { SiteTitle } from "../siteKit/SiteText";
import { useStartHref } from "../siteKit/useStartHref";
import { PlatformHeroStage } from "./PlatformHeroStage";
import { PlatformIndex } from "./PlatformIndex";

const ROW_IMAGE_SIZES = "(max-width: 900px) 100vw, 640px";
/** 그리기 전 자리 높이(넓은 화면, 900px 이하) — 시안 `--cis` */
const MORE_OFFSCREEN_SIZE = [900, 900] as const;

const MORE_STYLES = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2,minmax(0,1fr))",
    gap: "clamp(24px,3vw,40px)",
    marginTop: "clamp(32px,4vw,48px)",
    [SITE_MEDIA.tablet]: { gridTemplateColumns: "1fr" },
    // 가로 터치 화면: 한 줄에 하나면 카드 그림 하나가 화면 두 개 높이다 — 넓은 화면처럼 두 칸
    [SITE_MEDIA.landscapeTouch]: {
      gridTemplateColumns: "repeat(2,minmax(0,1fr))",
    },
  },
} as const;

/** 플랫폼(`/accurate`, marketing-service-pages) — 시안 accurate.html 그대로(첫 화면은 새 캡처 영상 + 선명한 UI 덮개). */
export function PlatformPage() {
  const { t } = useTranslation("landing");
  const startHref = useStartHref();
  const siteTitleId = useId();
  const moreTitleId = useId();
  const sceneSteps = (key: "view" | "bridge" | "layers") =>
    t(`site.scenes.${key}.steps`, {
      returnObjects: true,
    }) as readonly SceneStepText[];
  return (
    <>
      <PageHero
        eyebrow={t("site.accurate.eyebrow")}
        title={t("site.accurate.title")}
        description={t("site.accurate.description")}
        actions={
          <>
            <Box
              component={Link}
              href={startHref}
              sx={SITE_BUTTON_STYLES.primaryLarge}
            >
              {t("site.accurate.start")}
            </Box>
            <Box
              component={Link}
              href="/inquiry"
              sx={SITE_BUTTON_STYLES.outlineLarge}
            >
              {t("site.accurate.ask")}
            </Box>
          </>
        }
        media={null}
        figure={<PlatformHeroStage />}
      />
      <PlatformIndex />
      <SceneSection
        id="view"
        eyebrow={t("site.scenes.view.eyebrow")}
        title={t("site.scenes.view.title")}
        lead={t("site.scenes.view.lead")}
        stepsLabel={t("site.scenes.view.stepsLabel")}
        steps={sceneSteps("view")}
        durations={TERRAIN_DURATIONS.view}
        viewLabel={t("site.scenes.view.viewLabel")}
      >
        {(context) => <TerrainSceneContent context={context} preset="view" />}
      </SceneSection>
      <SceneSection
        id="survey-tools"
        eyebrow={t("site.scenes.bridge.eyebrowPlatform")}
        title={t("site.scenes.bridge.title")}
        lead={t("site.scenes.bridge.lead")}
        stepsLabel={t("site.scenes.bridge.stepsLabel")}
        steps={sceneSteps("bridge")}
        durations={BRIDGE_DURATIONS}
        viewLabel={t("site.scenes.bridge.viewLabel")}
        reverse
        bridge
        after={
          <FeatureRows>
            <FeatureRow
              kicker={t("site.rows.pfLayers.kicker")}
              title={t("site.rows.pfLayers.title")}
              text={t("site.rows.pfLayers.text")}
              media={
                <SiteImage
                  name="pf_layers"
                  sizes={ROW_IMAGE_SIZES}
                  alt={t("site.rows.pfLayers.alt")}
                  placeholder="none"
                  style={{ "--op": "0% 50%" } as CSSProperties}
                />
              }
            />
            <FeatureRow
              kicker={t("site.rows.earthwork.kicker")}
              title={t("site.rows.earthwork.title")}
              text={t("site.rows.earthwork.text")}
              media={
                <SiteImage
                  name="ew_summary"
                  sizes={ROW_IMAGE_SIZES}
                  alt={t("site.rows.earthwork.alt")}
                  placeholder="none"
                />
              }
            />
            <FeatureRow
              kicker={t("site.rows.measure.kicker")}
              title={t("site.rows.measure.title")}
              text={t("site.rows.measure.text")}
              media={
                <SiteImage
                  name="measure_zoom"
                  sizes={ROW_IMAGE_SIZES}
                  alt={t("site.rows.measure.alt")}
                  placeholder="none"
                />
              }
            />
          </FeatureRows>
        }
      >
        {(context) => <BridgeSceneContent context={context} />}
      </SceneSection>
      <SceneSection
        id="layers"
        eyebrow={t("site.scenes.layers.eyebrow")}
        title={t("site.scenes.layers.title")}
        lead={t("site.scenes.layers.lead")}
        stepsLabel={t("site.scenes.layers.stepsLabel")}
        steps={sceneSteps("layers")}
        durations={TERRAIN_DURATIONS.layers}
        viewLabel={t("site.scenes.layers.viewLabel")}
      >
        {(context) => <TerrainSceneContent context={context} preset="layers" />}
      </SceneSection>
      <SiteSection id="site" labelledBy={siteTitleId}>
        <Box sx={SITE_WRAP}>
          <SectionHead
            titleId={siteTitleId}
            eyebrow={t("site.accurate.site.eyebrow")}
            title={t("site.accurate.site.title")}
            lead={t("site.accurate.site.lead")}
            center
          />
          <FeatureRows afterHead>
            <FeatureRow
              kicker={t("site.rows.issue.kicker")}
              title={t("site.rows.issue.title")}
              text={t("site.rows.issue.text")}
              ratio="4/3"
              media={
                <SiteImage
                  name="issue_side"
                  sizes={ROW_IMAGE_SIZES}
                  alt={t("site.rows.issue.alt")}
                  placeholder="none"
                />
              }
            />
            <FeatureRow
              kicker={t("site.rows.geolog.kicker")}
              title={t("site.rows.geolog.title")}
              text={t("site.rows.geolog.text")}
              media={
                <SiteImage
                  name="geolog_side"
                  sizes={ROW_IMAGE_SIZES}
                  alt={t("site.rows.geolog.alt")}
                  placeholder="none"
                />
              }
            />
            <FeatureRow
              kicker={t("site.rows.pano.kicker")}
              title={t("site.rows.pano.title")}
              text={t("site.rows.pano.text")}
              media={
                <SiteImage
                  name="roadview_full"
                  sizes={ROW_IMAGE_SIZES}
                  alt={t("site.rows.pano.alt")}
                  placeholder="none"
                />
              }
            />
            <FeatureRow
              kicker={t("site.rows.equip.kicker")}
              title={t("site.rows.equip.title")}
              text={t("site.rows.equip.text")}
              appShot={false}
              media={
                <SiteVideo
                  name="ws3d_sim2"
                  loading="lazy"
                  restartOnEnter
                  ariaLabel={t("site.rows.equip.alt")}
                />
              }
            />
          </FeatureRows>
        </Box>
      </SiteSection>
      <SiteSection labelledBy={moreTitleId} skipOffscreen={MORE_OFFSCREEN_SIZE}>
        <Box sx={SITE_WRAP}>
          <Box
            component="h2"
            id={moreTitleId}
            ref={revealRef}
            data-reveal="up"
            sx={SITE_TYPE.h3}
          >
            <SiteTitle text={t("site.accurate.more.title")} />
          </Box>
          <Box sx={MORE_STYLES.grid}>
            <LinkCard
              href="/survey"
              revealIndex={0}
              media={
                <SiteImage
                  name="drone_work"
                  sizes="(max-width: 900px) 100vw, 600px"
                  alt={t("site.accurate.more.survey.alt")}
                />
              }
              kicker={t("site.accurate.more.survey.kicker")}
              title={t("site.accurate.more.survey.title")}
              text={t("site.accurate.more.survey.text")}
            />
            <LinkCard
              href="/plan"
              revealIndex={1}
              media={
                <SiteFileImage
                  file="art/tile_plan.svg"
                  width={600}
                  height={400}
                  alt={t("site.accurate.more.plan.alt")}
                />
              }
              kicker={t("site.accurate.more.plan.kicker")}
              title={t("site.accurate.more.plan.title")}
              text={t("site.accurate.more.plan.text")}
            />
          </Box>
        </Box>
      </SiteSection>
      <ClosingContact
        eyebrow={t("site.accurate.closing.eyebrow")}
        title={<SiteTitle text={t("site.accurate.closing.title")} />}
        actions={
          <>
            <Box
              component={Link}
              href="/survey"
              sx={SITE_BUTTON_STYLES.primaryLarge}
            >
              {t("site.accurate.closing.more")}
            </Box>
            <Box
              component={Link}
              href="/inquiry"
              sx={SITE_BUTTON_STYLES.outlineLarge}
            >
              {t("site.accurate.closing.ask")}
            </Box>
          </>
        }
      />
    </>
  );
}
